import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import ConfirmationModal from '../common/ConfirmationModal';
import { Select, Input } from '../ui/form';
import { 
  SkeletonTableRow, 
  SkeletonTableCell, 
  SkeletonText, 
  SkeletonTextShort,
  SkeletonBadge,
  Pagination,
  PaginationButton,
  PaginationInfo
} from '../ui';

interface Company {
  _id: string;
  name: string;
  headquarters?: string;
  website?: string;
  industry?: string;
  created_at: string;
  updated_at: string;
}

const INDUSTRIES = [
  'Automotive',
  'Bancario-Assicurativo',
  'Beauty',
  'Carburanti',
  'Consulenza',
  'Elettronica',
  'Energia',
  'Finanziario',
  'Fitness',
  'Food & Beverage',
  'Immobiliare',
  'Industria',
  'Moda',
  'Pet Care',
  'Pharma',
  'Retail',
  'Tech',
  'Telefonia'
];

interface Contact {
  _id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  role?: string;
  location?: string;
  company_id?: string | {
    _id: string;
    name: string;
    industry?: string;
  };
  sales_rep_id?: string | {
    _id: string;
    firstName: string;
    lastName: string;
  };
  sales_rep?: {
    _id: string;
    firstName: string;
    lastName: string;
  } | null;
  last_activity_at?: string;
  status_outcome?: 'positivo' | 'negativo' | 'in_corso';
  notes?: string;
  created_at: string;
  updated_at: string;
}

const Container = styled.div`
  max-width: 1800px;
  margin: 0 auto;
  padding: 12px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 2px solid #e5e7eb;
`;

const BackButton = styled.button`
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 8px 16px;
  color: #374151;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
  
  &:hover {
    background: #e5e7eb;
  }
`;

const Title = styled.h1`
  color: #1f2937;
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  background: ${props => props.variant === 'primary' ? '#667eea' : '#f3f4f6'};
  color: ${props => props.variant === 'primary' ? 'white' : '#374151'};
  border: 1px solid ${props => props.variant === 'primary' ? '#667eea' : '#d1d5db'};
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.variant === 'primary' ? '#5a67d8' : '#e5e7eb'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 30px;
`;

const MainContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
`;

// Removed unused FormGroup and FormLabel styled components

const ContactsSection = styled.div`
  margin-top: 40px;
`;

const SectionTitle = styled.h2`
  color: #1f2937;
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 20px;
`;

const ContactsTable = styled.div`
  overflow-x: auto;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  --skeleton-line-height: 22.4px;
  --table-grid-columns: minmax(140px, 1.2fr) minmax(120px, 0.9fr) minmax(180px, 2fr) minmax(100px, 0.7fr) minmax(120px, 0.9fr);
  --table-grid-gap: 0.5rem;
  --table-row-padding: 1rem;
  --table-row-border: 1px solid #e9ecef;
  --table-row-background: white;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns:
    minmax(140px, 1.2fr) /* Name */
    minmax(120px, 0.9fr) /* Email */
    minmax(180px, 2fr)   /* Role */
    minmax(100px, 0.7fr) /* Status */
    minmax(120px, 0.9fr) /* Created */;
  gap: 0.5rem;
  background: #f8f9fa;
  padding: 1rem;
  border-bottom: 2px solid #e9ecef;
  font-weight: 600;
  color: #495057;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns:
    minmax(140px, 1.2fr) /* Name */
    minmax(120px, 0.9fr) /* Email */
    minmax(180px, 2fr)   /* Role */
    minmax(100px, 0.7fr) /* Status */
    minmax(120px, 0.9fr) /* Created */;
  gap: 0.5rem;
  padding: 1rem;
  border-bottom: 1px solid #e9ecef;
  transition: background-color 0.2s;
  cursor: pointer;
  background: white;
  align-items: center;
  
  &:hover {
    background: #f8f9fa;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.div`
  display: flex;
  align-items: center;
  color: #1f2937;
  font-size: 14px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  position: relative;
  line-height: 1.5;
  
  a {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  &:hover::after {
    content: attr(title);
    position: absolute;
    bottom: 100%;
    left: 0;
    background: #1f2937;
    color: white;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 12px;
    white-space: normal;
    z-index: 1000;
    max-width: 300px;
    word-wrap: break-word;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    display: ${props => props.title && props.title.length > 20 ? 'block' : 'none'};
  }
  
  &:hover::before {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 10px;
    margin-bottom: -5px;
    border: 5px solid transparent;
    border-top-color: #1f2937;
    z-index: 1001;
    display: ${props => props.title && props.title.length > 20 ? 'block' : 'none'};
  }
`;

const StatusBadge = styled.span<{ status?: string }>`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  text-transform: capitalize;
  
  ${props => {
    switch (props.status) {
      case 'nuovo':
        return `
          background-color: #f5f5f5;
          color: #666;
        `;
      case 'positivo':
        return `
          background-color: #e8f5e8;
          color: #2e7d32;
        `;
      case 'negativo':
        return `
          background-color: #ffebee;
          color: #c62828;
        `;
      case 'in_corso':
        return `
          background-color: #fff9c4;
          color: #f57f17;
        `;
      default:
        return `
          background-color: #f5f5f5;
          color: #666;
        `;
    }
  }}
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #6b7280;
  font-size: 16px;
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
`;

const SuccessMessage = styled.div`
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #16a34a;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
  --skeleton-line-height: 25.6px;
`;

const InfoCard = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
`;

const InfoLabel = styled.div`
  color: #6b7280;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
`;

const InfoValue = styled.div`
  color: #1f2937;
  font-size: 16px;
  font-weight: 600;
`;

const getStatusLabel = (status: string | undefined): string => {
  switch (status) {
    case 'nuovo': return 'Nuovo';
    case 'positivo': return 'Positivo';
    case 'negativo': return 'Negativo';
    case 'in_corso': return 'In Corso';
    default: return 'Nuovo';
  }
};

const CompanyDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, hasRole } = useAuth();
  const isCreateMode = !id || id === 'new';

  // Helper to get initial state from URL
  const getInitialParam = (key: string, defaultVal: string = '') => {
    return new URLSearchParams(location.search).get(key) || defaultVal;
  };

  const [company, setCompany] = useState<Company | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(!isCreateMode);
  const [contactsLoading, setContactsLoading] = useState(!isCreateMode);
  const [contactsSearchTerm, setContactsSearchTerm] = useState(() => getInitialParam('contactsSearch'));
  const [currentPage, setCurrentPage] = useState(() => {
    const p = parseInt(getInitialParam('contactsPage', '1'), 10);
    return isNaN(p) || p < 1 ? 1 : p;
  });
  const isFirstMount = useRef(true);
  const [totalPages, setTotalPages] = useState(1);
  const [totalContacts, setTotalContacts] = useState(0);
  const pageSize = 10;
  const expectedRowCount = totalContacts > 0
    ? Math.min(pageSize, Math.max(totalContacts - (currentPage - 1) * pageSize, 0))
    : 0;
  const skeletonRowCount = expectedRowCount > 0
    ? expectedRowCount
    : (contacts.length > 0 ? contacts.length : pageSize);
  const pendingScrollY = useRef<number | null>(null);
  
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(isCreateMode);
  const [hasChanges, setHasChanges] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    headquarters: '',
    website: '',
    industry: ''
  });

  // Scroll to top when entering detail page
  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    fetchCompany();
  }, [id, isCreateMode]);

  useEffect(() => {
    fetchCompanyContacts();
  }, [id, currentPage, contactsSearchTerm, isCreateMode]);

  useEffect(() => {
    if (!contactsLoading && pendingScrollY.current !== null) {
      window.scrollTo({ top: pendingScrollY.current, behavior: 'auto' });
      pendingScrollY.current = null;
    }
  }, [contactsLoading]);

  // Sync sub-table state to URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (contactsSearchTerm) { params.set('contactsSearch', contactsSearchTerm); } else { params.delete('contactsSearch'); }
    if (currentPage > 1) { params.set('contactsPage', currentPage.toString()); } else { params.delete('contactsPage'); }
    navigate(`?${params.toString()}`, { replace: true });
  }, [contactsSearchTerm, currentPage]);

  const fetchCompany = async () => {
    if (isCreateMode || !id) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/companies/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data?.company) {
          setCompany(result.data.company);
        } else {
          setError('Invalid response format');
          return;
        }
        const data = result.data.company;
        setFormData({
          name: data.name || '',
          headquarters: data.headquarters || '',
          website: data.website || '',
          industry: data.industry || ''
        });
      } else {
        setError('Failed to load company details');
      }
    } catch (error) {
      setError('An error occurred while loading company details');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyContacts = async () => {
    if (isCreateMode || !id) {
      setContacts([]);
      setTotalPages(1);
      setTotalContacts(0);
      setContactsLoading(false);
      return;
    }
    try {
      setContactsLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        company_id: id || '',
        page: currentPage.toString(),
        limit: pageSize.toString()
      });
      if (contactsSearchTerm) {
        params.append('search', contactsSearchTerm);
      }
      
      const response = await fetch(`/api/contacts?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data?.contacts) {
          setContacts(result.data.contacts);
          // Handle pagination data if available, otherwise default
          setTotalPages(result.data.totalPages || result.data.pagination?.totalPages || 1);
          setTotalContacts(result.data.totalContacts || result.data.pagination?.totalContacts || result.data.pagination?.total || result.data.contacts.length);
        } else {
          setContacts([]);
          setTotalPages(1);
          setTotalContacts(0);
        }
      } else {
        console.error('Failed to load company contacts');
      }
    } catch (error) {
      console.error('An error occurred while loading company contacts');
    } finally {
      setContactsLoading(false);
    }
  };

  const salesRepNames = useMemo(() => {
    const ids = new Set<string>();
    const names: string[] = [];
    contacts.forEach(c => {
      const rep = c.sales_rep;
      if (rep && rep._id && !ids.has(rep._id)) {
        ids.add(rep._id);
        names.push(`${rep.firstName} ${rep.lastName}`);
      }
    });
    return names;
  }, [contacts]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setError('');
      setSuccess('');
      const token = localStorage.getItem('token');
      const response = await fetch(isCreateMode ? '/api/companies' : `/api/companies/${id}`, {
        method: isCreateMode ? 'POST' : 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          onboarding_source: 'manual'
        })
      });

      if (response.ok) {
        const result = await response.json();
        const createdId = result?.data?.company?._id || result?.data?._id;
        setSuccess(isCreateMode ? 'Azienda creata con successo' : 'Azienda aggiornata con successo');
        setHasChanges(false);
        if (isCreateMode && createdId) {
          navigate(`/dashboard/companies/${createdId}`);
          return;
        }
        setIsEditing(false);
        fetchCompany();
      } else {
        const data = await response.json();
        setError(data.message || (isCreateMode ? 'Impossibile creare l\'azienda' : 'Impossibile aggiornare l\'azienda'));
      }
    } catch (error) {
      setError(isCreateMode
        ? 'Si è verificato un errore durante la creazione dell\'azienda'
        : 'Si è verificato un errore durante l\'aggiornamento dell\'azienda'
      );
    }
  };

  const handleCancel = () => {
    if (isCreateMode) {
      navigate('/dashboard/companies');
      return;
    }
    if (company) {
      setFormData({
        name: company.name || '',
        headquarters: company.headquarters || '',
        website: company.website || '',
        industry: company.industry || ''
      });
    }
    setIsEditing(false);
    setHasChanges(false);
    setError('');
    setSuccess('');
  };

  const handleDeleteCompany = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/companies/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setSuccess('Azienda eliminata con successo');
        setTimeout(() => {
          navigate('/dashboard/companies');
        }, 1500);
      } else {
        const result = await response.json();
        setError(result.message || 'Impossibile eliminare l\'azienda');
      }
    } catch (error) {
      setError('Si è verificato un errore durante l\'eliminazione dell\'azienda');
    } finally {
      setShowDeleteModal(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handlePageChange = (nextPage: number) => {
    pendingScrollY.current = window.scrollY;
    setCurrentPage(nextPage);
  };

  const formatWebsiteUrl = (website: string) => {
    if (!website) return '';
    return website.startsWith('http') ? website : `https://${website}`;
  };
  
  // Check if user can edit this company
  const canEditCompany = (): boolean => {
    if (isCreateMode) return hasRole(['admin', 'super_user']);
    if (!company) return false;
    if (hasRole(['admin', 'super_user'])) return true;
    // Regular users can only edit companies if they're sales rep of connected contacts
    return contacts.some(contact => {
      const salesRepId = typeof contact.sales_rep_id === 'object' && contact.sales_rep_id 
        ? contact.sales_rep_id._id 
        : contact.sales_rep_id;
      return salesRepId === user?._id;
    });
  };
  const isInitialLoading = loading && !company;

  return (
    <Container>
      {!loading && !company && !isCreateMode && (
        <ErrorMessage>
          Azienda non trovata
        </ErrorMessage>
      )}

      {(loading || company || isCreateMode) && (
        <>
          <Header>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <BackButton onClick={() => navigate(-1)}>
            ⬅️ Torna alle Aziende
          </BackButton>
        </div>
        {(isCreateMode || (company && !contactsLoading)) && canEditCompany() && (
          <ActionButtons>
            {isEditing ? (
              <>
                <ActionButton onClick={handleCancel}>
                  ❌ Annulla
                </ActionButton>
                <ActionButton 
                  variant="primary" 
                  onClick={handleSave}
                  disabled={!hasChanges}
                >
                  {isCreateMode ? '💾 Salva Azienda' : '💾 Salva Modifiche'}
                </ActionButton>
              </>
            ) : (
              <>
                <ActionButton 
                  variant="primary" 
                  onClick={() => setIsEditing(true)}
                >
                  ✏️ Modifica Azienda
                </ActionButton>
                <ActionButton 
                  onClick={() => setShowDeleteModal(true)}
                  style={{ background: '#dc2626', color: 'white', borderColor: '#dc2626' }}
                >
                  🗑️ Elimina Azienda
                </ActionButton>
              </>
            )}
          </ActionButtons>
        )}
          </Header>
      
          <div style={{ borderBottom: '1px solid #e5e7eb', margin: '0 0 20px 0' }}></div>
      
          <Title style={{ marginBottom: '20px' }}>
            {isCreateMode ? 'Nuova Azienda' : (isInitialLoading ? <SkeletonText /> : (company?.name || '—'))}
          </Title>

      {error && <ErrorMessage>{error}</ErrorMessage>}
      {success && <SuccessMessage>{success}</SuccessMessage>}

          <ContentGrid>
        <MainContent>
          <InfoGrid>
            <InfoCard>
              <InfoLabel>Nome Azienda</InfoLabel>
              {isEditing ? (
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Inserisci nome azienda"
                  fullWidth
                />
              ) : (
                isInitialLoading ? <SkeletonTextShort /> : <InfoValue>{company?.name || 'Non specificato'}</InfoValue>
              )}
            </InfoCard>
            
            <InfoCard>
              <InfoLabel>Sede</InfoLabel>
              {isEditing ? (
                <Input
                  type="text"
                  name="headquarters"
                  value={formData.headquarters}
                  onChange={handleInputChange}
                  placeholder="es. Milano, MI"
                  fullWidth
                />
              ) : (
                isInitialLoading ? <SkeletonTextShort /> : <InfoValue>{company?.headquarters || 'Non specificato'}</InfoValue>
              )}
            </InfoCard>
            
            <InfoCard>
              <InfoLabel>Sito Web</InfoLabel>
              {isEditing ? (
                <Input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleInputChange}
                  placeholder="https://example.com"
                  fullWidth
                />
              ) : (
                isInitialLoading ? (
                  <SkeletonTextShort />
                ) : (
                  <InfoValue>
                    {company?.website ? (
                      <a 
                        href={formatWebsiteUrl(company.website)} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ color: '#667eea', textDecoration: 'none' }}
                      >
                        {company.website}
                      </a>
                    ) : (
                      'Non specificato'
                    )}
                  </InfoValue>
                )
              )}
            </InfoCard>
            
            <InfoCard>
              <InfoLabel>Settore</InfoLabel>
              {isEditing ? (
                <Select
                  name="industry"
                  value={formData.industry}
                  onChange={handleInputChange}
                  fullWidth
                  size="medium"
                >
                  <option value="">Seleziona settore</option>
                  {INDUSTRIES.map(industry => (
                    <option key={industry} value={industry}>
                      {industry}
                    </option>
                  ))}
                </Select>
              ) : (
                isInitialLoading ? <SkeletonTextShort /> : <InfoValue>{company?.industry || 'Non specificato'}</InfoValue>
              )}
            </InfoCard>

            <InfoCard>
              <InfoLabel>Commerciali</InfoLabel>
              {isInitialLoading ? <SkeletonTextShort /> : <InfoValue>{salesRepNames.length ? salesRepNames.join(', ') : 'Non specificato'}</InfoValue>}
            </InfoCard>
            
            <InfoCard>
              <InfoLabel>Creato il</InfoLabel>
              {isInitialLoading ? <SkeletonTextShort /> : <InfoValue>{company ? formatDate(company.created_at) : '—'}</InfoValue>}
            </InfoCard>
            
            <InfoCard>
              <InfoLabel>Ultimo Aggiornamento</InfoLabel>
              {isInitialLoading ? <SkeletonTextShort /> : <InfoValue>{company ? formatDate(company.updated_at) : '—'}</InfoValue>}
            </InfoCard>
          </InfoGrid>
        </MainContent>
          </ContentGrid>

          {!isCreateMode && (
          <ContactsSection>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <SectionTitle style={{ marginBottom: 0 }}>Contatti Azienda ({totalContacts})</SectionTitle>
          <Input 
            type="text" 
            placeholder="Cerca contatti..." 
            value={contactsSearchTerm}
            onChange={(e) => {
              setContactsSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            style={{ maxWidth: '300px' }}
          />
        </div>
        <ContactsTable>
          <TableHeader>
            <div>Nome</div>
            <div>Email</div>
            <div>Ruolo</div>
            <div>Stato</div>
            <div>Creato il</div>
          </TableHeader>
          {contactsLoading ? (
            Array.from({ length: skeletonRowCount }).map((_, index) => (
              <SkeletonTableRow key={index}>
                <SkeletonTableCell><SkeletonText /></SkeletonTableCell>
                <SkeletonTableCell><SkeletonTextShort /></SkeletonTableCell>
                <SkeletonTableCell><SkeletonTextShort /></SkeletonTableCell>
                <SkeletonTableCell><SkeletonBadge /></SkeletonTableCell>
                <SkeletonTableCell><SkeletonTextShort /></SkeletonTableCell>
              </SkeletonTableRow>
            ))
          ) : contacts.length === 0 ? (
            <EmptyState>
              Nessun contatto trovato per questa azienda.
            </EmptyState>
          ) : (
            contacts.map(contact => (
              <TableRow 
                key={contact._id} 
                onClick={() => navigate(`/dashboard/contacts/${contact._id}`)}
              >
                <TableCell title={`${contact.first_name} ${contact.last_name}`}>
                  {contact.first_name} {contact.last_name}
                </TableCell>
                <TableCell title={contact.email}>
                  <a 
                    href={`mailto:${contact.email}`} 
                    style={{ color: '#667eea', textDecoration: 'none' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {contact.email}
                  </a>
                </TableCell>
                <TableCell title={contact.role || 'Non specificato'}>
                  {contact.role || 'Non specificato'}
                </TableCell>
                <TableCell title={getStatusLabel(contact.status_outcome)}>
                  <StatusBadge status={contact.status_outcome || 'nuovo'}>
                    {getStatusLabel(contact.status_outcome)}
                  </StatusBadge>
                </TableCell>
                <TableCell title={formatDate(contact.created_at)}>
                  {formatDate(contact.created_at)}
                </TableCell>
              </TableRow>
            ))
          )}
        </ContactsTable>

        <Pagination>
          <PaginationButton
            disabled={contactsLoading || currentPage === 1}
            onClick={() => handlePageChange(currentPage - 1)}
          >
            Precedente
          </PaginationButton>
          <PaginationInfo>
            Pagina {currentPage} di {totalPages} ({totalContacts} contatti)
          </PaginationInfo>
          <PaginationButton
            disabled={contactsLoading || currentPage === totalPages}
            onClick={() => handlePageChange(currentPage + 1)}
          >
            Successivo
          </PaginationButton>
        </Pagination>
          </ContactsSection>
          )}

          {company && (
            <ConfirmationModal
              isOpen={showDeleteModal}
              title="Elimina Azienda"
              message={`Sei sicuro di voler eliminare ${company.name}? Questa azione eliminerà logicamente l'azienda e tutti i contatti associati avranno il riferimento all'azienda rimosso. Questa azione può essere annullata da un amministratore.`}
              confirmText="Elimina Azienda"
              cancelText="Annulla"
              onConfirm={handleDeleteCompany}
              onCancel={() => setShowDeleteModal(false)}
              variant="danger"
            />
          )}
        </>
      )}
    </Container>
  );
};

export default CompanyDetails;
